/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { organizationLogoQuery, organizationQuery } from '@/api/organization';
import amida from '@/assets/amida.png';

/**
 * The logo uploaded for the app on Company details, while it loads and after.
 *
 * The app's logo, never the one on documents: they are different images. The
 * details are read from the cache only, never fetched here -- the sign-in page
 * has no session to fetch them with -- and when they are there their
 * `app_logo_tag` makes a new logo a new address, so an upload shows at once.
 *
 * `pending` holds the space with the built-in mark unseen, so a page with an
 * uploaded logo does not flash the other one first. `failed` remembers an
 * address that would not draw, so the built-in mark is shown instead of a
 * broken image.
 */
function useUploadedLogo(): {
  src: string | null;
  pending: boolean;
  failed: (src: string) => void;
} {
  const { data: organization } = useQuery({ ...organizationQuery(), enabled: false });
  const { data, isPending } = useQuery(
    organizationLogoQuery(organization?.app_logo_tag ?? null),
  );
  const [broken, setBroken] = useState<string | null>(null);
  return {
    src: data && data !== broken ? data : null,
    pending: isPending,
    failed: setBroken,
  };
}

/** The quill's feather, traced from the logo (1024 px, potrace units). */
const QUILL_FEATHER =
  'M7795 9604 c-11 -2 -135 -17 -275 -34 -953 -114 -1671 -290 -2280 -558 -186 -81 -258 -119 -240 -126 8 -2 132 -26 274 -53 143 -26 253 -49 245 -50 -8 -2 -165 3 -349 10 l-335 14 -60 -36 c-209 -121 -482 -332 -678 -520 -155 -151 -262 -273 -399 -457 -110 -147 -292 -414 -286 -419 2 -2 176 -33 388 -69 212 -37 390 -69 395 -71 6 -3 -188 -2 -430 2 l-441 6 -101 -179 c-123 -217 -275 -519 -379 -755 -73 -167 -227 -550 -221 -554 1 -1 99 -18 217 -38 l215 -35 -232 -1 -231 -1 -61 -182 c-232 -703 -384 -1402 -445 -2046 l-13 -142 -407 -608 -406 -607 -105 -304 c-57 -168 -104 -306 -102 -307 2 -2 207 162 406 326 102 83 110 93 390 477 167 228 292 391 301 390 22 -1 363 196 590 341 734 469 1397 1052 2016 1772 148 172 402 485 410 506 3 5 -41 142 -96 303 -55 161 -100 296 -100 300 0 3 59 -106 132 -244 72 -137 133 -252 135 -255 6 -7 24 18 166 225 244 354 520 819 805 1356 l152 285 -60 179 c-33 98 -59 180 -57 181 1 1 36 -63 77 -142 41 -79 77 -140 81 -136 4 4 73 135 154 292 377 728 591 1101 800 1390 156 217 339 415 489 528 l69 52 -49 -1 c-27 -1 -58 -3 -69 -5z m-530 -338 c61 -26 74 -91 28 -143 -16 -16 -84 -79 -153 -139 -454 -394 -1028 -967 -1546 -1544 -456 -508 -964 -1123 -1485 -1800 -350 -454 -950 -1285 -1359 -1883 -312 -455 -317 -461 -384 -433 -39 16 -56 42 -56 84 0 64 1040 1540 1604 2277 1119 1463 2182 2636 3157 3482 87 76 129 106 157 112 1 1 18 -5 37 -13z';

/** The signature the quill draws. */
const QUILL_INK =
  'M4684 3620 c-149 -39 -317 -145 -409 -259 -141 -175 -204 -387 -192 -645 12 -263 95 -519 264 -809 l28 -47 -55 -50 c-433 -393 -972 -643 -1529 -710 -136 -16 -426 -14 -553 4 -385 55 -688 196 -945 440 -53 49 -76 66 -85 59 -7 -6 -44 -35 -82 -66 -38 -31 -71 -55 -74 -53 -2 3 12 50 31 104 l36 100 -22 6 c-97 26 -213 -33 -258 -129 -24 -52 -24 -138 0 -190 24 -53 189 -217 312 -310 290 -218 612 -350 1009 -412 143 -22 553 -25 700 -5 651 91 1240 358 1717 781 l93 81 97 -81 c432 -359 976 -537 1573 -516 473 18 761 95 1575 422 621 249 863 318 1165 331 165 7 209 17 260 62 54 46 80 101 80 172 0 71 -26 126 -80 173 -64 55 -115 63 -307 48 -376 -28 -619 -97 -1282 -361 -621 -248 -807 -308 -1106 -361 -150 -27 -485 -37 -632 -20 -387 46 -725 193 -991 432 l-50 45 74 115 c243 372 367 796 325 1114 -53 400 -342 626 -687 535z m180 -451 c16 -13 32 -41 42 -76 22 -76 15 -267 -14 -375 -48 -179 -191 -480 -217 -458 -21 18 -95 216 -116 312 -28 128 -23 316 10 402 37 93 114 164 219 202 48 17 45 17 76 -7z';

/**
 * Vellum's own mark: a quill signing its name, the same as the browser tab's
 * icon. Drawn inline so its colours follow the theme (`.quill` in the
 * stylesheet): a navy feather in light, a lit one in dark.
 */
export function VellumQuill({ className = 'quill' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="26 26 971 971"
      aria-hidden="true"
      focusable="false"
    >
      <g transform="translate(0 1024) scale(0.1 -0.1)">
        <path className="quill-feather" d={QUILL_FEATHER} />
        <path className="quill-ink" d={QUILL_INK} />
      </g>
    </svg>
  );
}

/**
 * Amida's logo.
 *
 * The one uploaded for the app on Company details when there is one -- fetched from
 * Vellum's own public endpoint, so the sign-in page can show it too -- and
 * otherwise the built-in wordmark, which is also what shows if the upload
 * cannot be drawn. The built-in mark is flipped to white in the dark theme by
 * a filter in the stylesheet; an uploaded one keeps its colours on a small
 * paper-white plate instead (`.brand-upload`).
 */
export function AmidaMark({
  alt = 'Amida Technology Solutions',
  className = 'amida-mark',
}: {
  alt?: string;
  /** The sign-in card sets its own (`auth-logo`): white on the dark ground, and
      taller than the one in the rail. */
  className?: string;
}) {
  const logo = useUploadedLogo();
  if (logo.src) {
    const src = logo.src;
    return (
      <img
        src={src}
        alt={alt}
        className={`${className} brand-upload`}
        onError={() => {
          logo.failed(src);
        }}
      />
    );
  }
  return (
    <img
      src={amida}
      alt={alt}
      className={className}
      {...(logo.pending ? { 'data-pending': '' } : {})}
    />
  );
}

/**
 * Just the `A`, for the collapsed sidebar.
 *
 * The built-in file, cropped by its container: `.mark-a` is 24px wide with the
 * image sitting 90px wide and pinned left, so what shows through is the
 * initial. An uploaded logo has no known initial to crop to, so it is shown
 * whole, fitted to the same small square.
 *
 * Deliberately carries no `amida-mark` class — that one is hidden when the
 * rail collapses, which is exactly when this needs to appear.
 */
export function AmidaGlyph() {
  const logo = useUploadedLogo();
  const src = logo.src;
  return (
    <span className="mark-a">
      {src ? (
        <img
          src={src}
          alt=""
          className="brand-upload"
          onError={() => {
            logo.failed(src);
          }}
        />
      ) : (
        <img src={amida} alt="" {...(logo.pending ? { 'data-pending': '' } : {})} />
      )}
    </span>
  );
}
